// Conversaciones y clientes sobre el webmail (capa IA, backend /admin/inbox).
// Sólo cubre correos triados desde 2026-09-27: los hilos se arman con las
// cabeceras Message-ID / In-Reply-To / References que se guardan desde entonces.

import { useEffect, useState } from 'react';
import { ChevronLeft, MessagesSquare, Pencil, Users } from 'lucide-react';

import {
  fetchClientThreads, fetchClients, fetchThread, renameClient,
  type ClientInfo, type ThreadInfo, type ThreadMsg,
} from '../api';

function shortDate(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
  } catch { return ''; }
}

/** Resumen de la conversación + sus correos, dentro del lector. */
export function ThreadBox({ threadKey, count, summary, currentUid, onOpenUid }: {
  threadKey: string; count: number; summary: string; currentUid: string;
  onOpenUid: (uid: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<ThreadMsg[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => { setOpen(false); setMsgs(null); setError(''); }, [threadKey]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !msgs) {
      try { setMsgs((await fetchThread(threadKey)).messages); }
      catch { setError('No se pudo cargar la conversación.'); }
    }
  };

  return (
    <div className="mb-4 rounded-xl border border-border bg-card p-3">
      <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-foreground">
        <MessagesSquare size={13} className="text-primary" /> Conversación · {count} correos
      </div>
      <p className="text-sm leading-relaxed text-foreground/90">
        {summary || 'El resumen de la conversación se está generando…'}
      </p>
      <button onClick={toggle} className="mt-2 text-xs font-medium text-primary hover:underline">
        {open ? 'Ocultar correos de la conversación' : `Ver los ${count} correos`}
      </button>
      {open && (
        <div className="mt-2 divide-y divide-border/60 rounded-lg border border-border">
          {error && <div className="px-3 py-2 text-xs text-destructive">{error}</div>}
          {!msgs && !error && <div className="px-3 py-2 text-xs text-muted-foreground">Cargando…</div>}
          {(msgs ?? []).map((m) => (
            <button key={m.item_id} disabled={!m.uid || m.uid === currentUid}
              onClick={() => m.uid && onOpenUid(m.uid)}
              className={`block w-full px-3 py-2 text-left hover:bg-muted/50 disabled:cursor-default ${
                m.uid === currentUid ? 'bg-muted/60' : ''}`}>
              <div className="flex items-center gap-2 text-xs">
                <span className="truncate font-medium text-foreground">{m.from_name || m.from_addr}</span>
                <span className="ml-auto shrink-0 text-muted-foreground">{shortDate(m.received_at)}</span>
              </div>
              <div className="line-clamp-2 text-xs text-muted-foreground">{m.summary || m.subject}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Vista "Clientes": clientes → conversaciones → abrir el último correo. */
export function ClientsPane({ onOpenUid }: { onOpenUid: (uid: string) => void }) {
  const [clients, setClients] = useState<ClientInfo[] | null>(null);
  const [current, setCurrent] = useState<ClientInfo | null>(null);
  const [threads, setThreads] = useState<ThreadInfo[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchClients().then(setClients).catch(() => setError('No se pudieron cargar los clientes.'));
  }, []);

  const openClient = async (c: ClientInfo) => {
    setCurrent(c); setThreads(null); setError('');
    try { setThreads(await fetchClientThreads(c.client_key)); }
    catch { setError('No se pudieron cargar las conversaciones.'); }
  };

  const saveName = async (c: ClientInfo, name: string) => {
    try {
      const r = await renameClient(c.client_key, name);
      setClients((prev) => (prev ?? []).map((x) => (x.client_key === c.client_key ? { ...x, name: r.name } : x)));
    } catch { setError('No se pudo guardar el nombre.'); }
  };

  if (current) {
    return (
      <div>
        <button onClick={() => { setCurrent(null); setThreads(null); }}
          className="flex w-full items-center gap-1 border-b border-border px-3 py-2 text-left text-sm font-medium text-foreground hover:bg-muted/50">
          <ChevronLeft size={14} /> {current.name}
        </button>
        {error && <div className="px-3 py-2 text-xs text-destructive">{error}</div>}
        {!threads && !error && <div className="p-6 text-center text-sm text-muted-foreground">Cargando…</div>}
        {(threads ?? []).map((t) => (
          <button key={t.thread_key} disabled={!t.latest_uid} onClick={() => t.latest_uid && onOpenUid(t.latest_uid)}
            className="block w-full border-b border-border/60 px-3 py-2.5 text-left hover:bg-muted/50">
            <div className="flex items-center gap-2">
              <span className="truncate text-sm font-medium text-foreground">{t.subject || '(sin asunto)'}</span>
              {t.message_count > 1 && (
                <span className="shrink-0 rounded-full bg-muted px-1.5 text-[10px] text-muted-foreground">{t.message_count}</span>
              )}
              <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">{shortDate(t.last_at)}</span>
            </div>
            {t.summary && <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{t.summary}</div>}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div>
      {error && <div className="px-3 py-2 text-xs text-destructive">{error}</div>}
      {!clients && !error && <div className="p-6 text-center text-sm text-muted-foreground">Cargando…</div>}
      {clients && clients.length === 0 && (
        <div className="p-8 text-center text-muted-foreground">
          <Users size={26} className="mx-auto mb-2 opacity-40" />
          <p className="text-sm">Aún no hay clientes. Se agrupan con los correos que llegan desde el 27 de septiembre.</p>
        </div>
      )}
      {(clients ?? []).map((c) => (
        <div key={c.client_key} className="flex items-center gap-2 border-b border-border/60 px-3 py-2.5 hover:bg-muted/50">
          <button onClick={() => openClient(c)} className="min-w-0 flex-1 text-left">
            <div className="truncate text-sm font-medium text-foreground">{c.name}</div>
            <div className="text-xs text-muted-foreground">
              {c.threads} conversación{c.threads === 1 ? '' : 'es'} · {shortDate(c.last_at)}
              {c.name !== c.client_key && <span className="ml-1 opacity-70">· {c.client_key}</span>}
            </div>
          </button>
          <RenameButton client={c} onSave={(name) => saveName(c, name)} />
        </div>
      ))}
    </div>
  );
}

function RenameButton({ client, onSave }: { client: ClientInfo; onSave: (name: string) => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(client.name === client.client_key ? '' : client.name);
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} title="Ponerle nombre al cliente" aria-label="Ponerle nombre al cliente"
        className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground">
        <Pencil size={13} />
      </button>
    );
  }
  return (
    <form className="flex shrink-0 items-center gap-1"
      onSubmit={async (e) => { e.preventDefault(); await onSave(value); setOpen(false); }}>
      <input autoFocus value={value} onChange={(e) => setValue(e.target.value)} placeholder={client.client_key}
        className="w-28 rounded-md border border-border bg-background px-1.5 py-0.5 text-xs text-foreground outline-none" />
      <button type="submit" className="rounded-md bg-primary px-1.5 py-0.5 text-[11px] text-primary-foreground">OK</button>
    </form>
  );
}
